import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { User, Customer } from './types';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { api } from './services/api';
import { ToastProvider } from './components/ui';
import * as realtime from './services/realtime';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  // Track whether we currently believe the user is signed in, for the
  // unauthorized handler to decide if we should show a "session expired" hint.
  const wasSignedInRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('ottosbarn_token');
    const savedUser = localStorage.getItem('ottosbarn_user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        wasSignedInRef.current = true;
        loadData();
      } catch {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime updates: connect Socket.IO when signed in, refetch on every
  // data:changed event from the server (debounced so bursty edits coalesce).
  useEffect(() => {
    if (!user) {
      realtime.disconnect();
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    realtime.connect((_evt) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        loadData();
      }, 250);
    });
    return () => {
      if (timer) clearTimeout(timer);
      realtime.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-logout on any 401/403 from the API.
  useEffect(() => {
    const onUnauthorized = () => {
      if (wasSignedInRef.current) {
        setLoginNotice('Your session expired. Please sign in again.');
      }
      wasSignedInRef.current = false;
      localStorage.removeItem('ottosbarn_user');
      localStorage.removeItem('ottosbarn_token');
      setUser(null);
      setCustomers([]);
      setApiError(null);
      setLoading(false);
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  const loadData = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const data = await api.getCustomers();
      setCustomers(data);
    } catch (err: unknown) {
      console.error('API Error: Could not fetch customers', err);
      const status = (err as { status?: number })?.status;
      // 401/403 is handled by the auth:unauthorized listener — no banner.
      if (status === 401 || status === 403) return;
      setApiError(
        'The server is unreachable. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (u: User, token: string) => {
    setUser(u);
    wasSignedInRef.current = true;
    setLoginNotice(null);
    localStorage.setItem('ottosbarn_user', JSON.stringify(u));
    localStorage.setItem('ottosbarn_token', token);
    loadData();
  };

  const handleLogout = () => {
    setUser(null);
    wasSignedInRef.current = false;
    setLoginNotice(null);
    localStorage.removeItem('ottosbarn_user');
    localStorage.removeItem('ottosbarn_token');
    setCustomers([]);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-sand-50 flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-sage-600 text-white font-display text-base mb-4">
            O
          </span>
          <div className="h-1 w-32 bg-sand-200 rounded-full overflow-hidden">
            <div className="h-full w-1/2 bg-sage-500 rounded-full animate-pulse" />
          </div>
          <p className="text-sand-500 text-sm mt-3">Loading your CRM…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <ToastProvider>
        <Login onLogin={handleLogin} notice={loginNotice} />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      {apiError && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 bg-white border border-red-200 shadow-soft rounded-xl px-3.5 py-2 flex items-center gap-3 text-sm">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sand-800">{apiError}</span>
          <button
            onClick={() => loadData()}
            className="inline-flex items-center gap-1 text-sage-700 hover:text-sage-900 font-medium"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </button>
        </div>
      )}
      <Dashboard
        user={user}
        customers={customers}
        onLogout={handleLogout}
        setCustomers={setCustomers}
        refreshData={loadData}
      />
    </ToastProvider>
  );
};

export default App;
